import React, { useCallback, useContext, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import { GridFilterChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { isDocumentUrlExists } from 'api/Documents/Types/DocumentUrlNameDto'
import { UpdateWalletComplexTransfer } from 'api/Wallet/Types/UpdateWalletComplexTransfer'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid'
import { useApiClient } from 'utils/api/ApiClient'
import { DocumentApiClient } from 'utils/api/documentApi'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isFileTypeImage } from 'utils/check-file-type'
import { showMediaWindow } from 'utils/media-window'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import { DocumentPreviewModal } from '../../../Records/Clients/Details/ClientDetailsDocument/DocumentPreviewModal'
import {
  TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralForm,
  TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralProps,
} from './TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralForm'

export const TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralPage: React.FC<
  TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralProps & { forceRefresh(): void }
> = (props) => {
  const { setActiveTab, setAuditLogModal, permissions, client, transaction, forceRefresh } = props

  const setToast = useContext(ToastContext)
  const params = useParams<{ id: string }>()
  const location = useLocation()

  const [isEdit, setEdit] = useState(false)
  const [documentPreviewModal, setDocumentPreviewModal] = useState<string | null>(null)
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })

  const walletsApiClient = useApiClient(WalletsApiClient)
  const documentApiClient = useApiClient(DocumentApiClient)

  const notesCallback = useCallback(
    async () =>
      params.id
        ? await walletsApiClient.getTransactionNotesByTransactionId(params.id)
        : Promise.resolve(),
    [params.id] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const { data: notes = [] } = useFetchOne(notesCallback)

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState({ skip: event.page.skip, take: event.page.take })
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const showDocumentPreviewModal = async (document: {
    id: string
    name: string
  }): Promise<void> => {
    const documentFile = await documentApiClient.getDocumentUrl(document.id)

    if (isDocumentUrlExists(documentFile) && isFileTypeImage(documentFile.filename)) {
      showMediaWindow(documentFile.url, documentFile.filename)
    } else {
      setDocumentPreviewModal(document.id)
    }
  }

  const onSubmit = async (values: UpdateWalletComplexTransfer) => {
    await walletsApiClient.updateWalletComplexTransfer(values)
    setToast(successToast('Transaction updated successfully.'))
    forceRefresh()
  }

  return (
    <React.Fragment>
      {!!documentPreviewModal && (
        <DocumentPreviewModal
          document={{ id: documentPreviewModal }}
          onClose={() => setDocumentPreviewModal(null)}
        />
      )}
      <TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralForm
        transaction={transaction}
        client={client}
        filter={filter}
        paginationState={paginationState}
        permissions={permissions}
        path={location.pathname}
        setAuditLogModal={setAuditLogModal}
        setActiveTab={setActiveTab}
        showDocumentPreviewModal={showDocumentPreviewModal}
        handlePageChange={handlePageChange}
        handleFilterChange={handleFilterChange}
        onSubmit={onSubmit}
        notes={notes}
        isEdit={isEdit}
        setEdit={setEdit}
      />
    </React.Fragment>
  )
}
